/**
 * Desktop header nav scroller
 * Keeps logo + search/account/cart inside the viewport. When middle menu items
 * overflow, arrows appear on the right and each click slides one item.
 */
(function () {
  if (window.customElements.get('header-nav-scroller')) return;

  window.customElements.define('header-nav-scroller', class extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.header__nav-scroller__viewport');
      this.list = this.querySelector('.header__nav__list');
      this.prevBtn = this.querySelector('.header__nav-scroller__btn--prev');
      this.nextBtn = this.querySelector('.header__nav-scroller__btn--next');
      this.arrows = this.querySelector('.header__nav-scroller__arrows');
      this.nav = this.closest('.header__nav-container') || this;
      this.index = 0;
      this._scrolling = false;

      if (!this.viewport || !this.list) return;

      this._onPrev = this.go.bind(this, -1);
      this._onNext = this.go.bind(this, 1);
      this._onScroll = this._handleScroll.bind(this);
      this._onResize = this.refresh.bind(this);
      this._onItemEnter = this._positionDropdown.bind(this);
      this._onWinScroll = this._updateOpenDropdown.bind(this);

      if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNext);
      this.viewport.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize);
      window.addEventListener('scroll', this._onWinScroll, { passive: true });

      this._items().forEach(function (item) {
        item.addEventListener('mouseenter', this._onItemEnter);
      }, this);

      this._resizeObserver = new ResizeObserver(this._onResize);
      this._resizeObserver.observe(this.viewport);
      this._resizeObserver.observe(this.list);

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () {
          if (this.isConnected) this.refresh();
        }.bind(this));
      }

      this.refresh();
    }

    disconnectedCallback() {
      if (this.prevBtn && this._onPrev) this.prevBtn.removeEventListener('click', this._onPrev);
      if (this.nextBtn && this._onNext) this.nextBtn.removeEventListener('click', this._onNext);
      if (this.viewport && this._onScroll) this.viewport.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('scroll', this._onWinScroll);
      if (this._resizeObserver) this._resizeObserver.disconnect();
      this._items().forEach(function (item) {
        item.removeEventListener('mouseenter', this._onItemEnter);
      }, this);
    }

    _items() {
      if (!this.list) return [];
      return Array.prototype.slice.call(
        this.list.querySelectorAll(':scope > .header__nav__list-item, :scope > span > .header__nav__list-item')
      );
    }

    _maxScroll() {
      return Math.max(0, this.list.scrollWidth - this.viewport.clientWidth);
    }

    _itemScrollLeft(item) {
      if (!item) return 0;
      // Item offset relative to the list. Do not add viewport.scrollLeft —
      // getBoundingClientRect already moves with scroll, so adding it again
      // made "previous" target the current position and look like a no-op.
      return Math.round(
        item.getBoundingClientRect().left - this.list.getBoundingClientRect().left
      );
    }

    _currentIndexFromScroll() {
      const items = this._items();
      const scrollLeft = this.viewport.scrollLeft;
      let index = 0;

      for (let i = 0; i < items.length; i++) {
        if (this._itemScrollLeft(items[i]) <= scrollLeft + 2) index = i;
        else break;
      }

      return index;
    }

    _canScrollNext() {
      return this.viewport.scrollLeft < this._maxScroll() - 2;
    }

    _canScrollPrev() {
      return this.viewport.scrollLeft > 2;
    }

    _positionDropdown(event) {
      const item = event.currentTarget;
      if (!item) return;
      const header = this.closest('header.header__wrapper') || this.nav;
      const headerRect = header.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();

      // Simple dropdowns are positioned against the header, not the clipped viewport.
      item.style.setProperty('--header-submenu-left', Math.round(itemRect.left - headerRect.left) + 'px');
      item.style.setProperty('--header-dropdown-top', Math.round(itemRect.bottom - headerRect.top - 2) + 'px');
    }

    _updateOpenDropdown() {
      const items = this._items();
      for (let i = 0; i < items.length; i++) {
        if (items[i].matches(':hover')) {
          this._positionDropdown({ currentTarget: items[i] });
          return;
        }
      }
    }

    _handleScroll() {
      if (this._scrolling) return;
      this.index = this._currentIndexFromScroll();
      this._fitWholeItems();
      this.updateButtons();
      this._updateOpenDropdown();
    }

    _header() {
      return this.closest('header.header__wrapper');
    }

    // When the pill search steals the middle column, collapse it to an icon
    // so the scroll arrows have a real slot beside the menu.
    _syncTight() {
      const header = this._header();
      if (!header) return;
      const search = header.querySelector('.header-search');
      if (!search || search.classList.contains('is-open')) return;

      const box = search.querySelector('predictive-search') || search;
      const tight = header.classList.contains('header-nav-tight');
      if (!tight) this._searchWidth = box.getBoundingClientRect().width;

      const pill = Math.max(this._searchWidth || 180, 16);
      const delta = pill - 16;
      const middleIfPill = tight ? this.clientWidth - delta : this.clientWidth;
      const needsTight = this.list.scrollWidth > middleIfPill + 1;

      if (needsTight !== tight) header.classList.toggle('header-nav-tight', needsTight);
    }

    // Keep the viewport on whole menu items so a cut word cannot run into the arrows.
    _fitWholeItems() {
      if (!this.classList.contains('is-overflow') || !this.arrows) {
        this.viewport.style.maxWidth = '';
        return;
      }

      const available = Math.max(0, this.clientWidth - this.arrows.offsetWidth);
      const shift = this.viewport.scrollLeft;
      let edge = 0;

      this._items().forEach(function (item) {
        const left = this._itemScrollLeft(item);
        const right = left + item.offsetWidth;
        if (left >= shift - 1 && right - shift <= available + 1) edge = right - shift;
      }, this);

      this.viewport.style.maxWidth = edge > 8 ? Math.ceil(edge) + 'px' : '';
    }

    go(direction) {
      if (this._scrolling) return;

      const items = this._items();
      if (!items.length) return;

      const maxScroll = this._maxScroll();
      const currentIndex = this._currentIndexFromScroll();
      let nextIndex = currentIndex + direction;

      if (direction > 0 && !this._canScrollNext()) return;
      if (direction < 0 && !this._canScrollPrev()) return;

      nextIndex = Math.max(0, Math.min(nextIndex, items.length - 1));

      this.index = nextIndex;
      this._scrolling = true;
      this.viewport.scrollTo({
        left: nextIndex === 0 ? 0 : Math.min(this._itemScrollLeft(items[this.index]), maxScroll),
        behavior: 'smooth'
      });
      this.updateButtons();

      window.setTimeout(function () {
        this._scrolling = false;
        this.index = this._currentIndexFromScroll();
        this.refresh();
      }.bind(this), 400);
    }

    refresh() {
      if (!this.viewport || !this.list || this._scrolling || this._fitting) return;
      this._fitting = true;

      try {
        this.viewport.style.maxWidth = '';
        this._syncTight();

        // Compare against the middle column, not the viewport. With overflow
        // visible the viewport grows to the menu and would hide the arrows.
        const overflowing = this.list.scrollWidth > this.clientWidth + 1;
        this.classList.toggle('is-overflow', overflowing);

        if (!overflowing) {
          this.index = 0;
          this.viewport.scrollLeft = 0;
          this.viewport.style.maxWidth = '';
        } else {
          this._fitWholeItems();
        }

        this.updateButtons();
        this._updateOpenDropdown();
      } finally {
        this._fitting = false;
      }
    }

    updateButtons() {
      if (this.prevBtn) this.prevBtn.disabled = !this._canScrollPrev();
      if (this.nextBtn) this.nextBtn.disabled = !this._canScrollNext();
    }
  });
})();

/**
 * 点击头部菜单按钮：头部先变成实底，并在同一次点击里打开侧边导航。
 * 按钮在原位置过渡成关闭图标。交互对齐 rovdesy 的移动端菜单。
 */
(function () {
  var suppressClick = false;

  function menuButton(event) {
    return event.target.closest('.js-slideout-open[aria-controls="slideout-mobile-navigation"]');
  }

  function slideout() {
    return document.getElementById('slideout-mobile-navigation');
  }

  function isOpen(drawer) {
    return drawer.matches(':popover-open');
  }

  function visibleRect(node) {
    if (!node) return null;
    var rect = node.getBoundingClientRect();
    if (rect.height < 1 || rect.width < 1) return null;
    return rect;
  }

  function headerPieces() {
    return [
      document.querySelector('.header__mobile-wrapper'),
      document.querySelector('.mobile-header-search'),
      document.querySelector('header.header__wrapper'),
      document.querySelector('.sticky-header')
    ];
  }

  function headerBar() {
    return document.querySelector('.header__mobile-wrapper') || document.querySelector('header.header__wrapper');
  }

  function syncMenuTop() {
    var trigger = document.querySelector('.mobile-menu__trigger');
    var logo = document.querySelector('.header__mobile__logo-wrapper');
    var cart = document.querySelector('.header__mobile__cart-links-container');
    var search = document.querySelector('.mobile-header-search.is-active');
    var bottom = 0;
    [trigger, logo, cart, search].forEach(function (node) {
      var rect = visibleRect(node);
      if (rect) bottom = Math.max(bottom, rect.bottom);
    });
    if (bottom < 1) {
      var bar = headerBar();
      var rect = visibleRect(bar);
      if (rect) bottom = rect.bottom;
    }
    if (bottom < 1) return;
    document.documentElement.style.setProperty('--mobile-menu-top', Math.floor(bottom) + 'px');
  }

  var stuckForMenu = false;
  var scrollBeforeStick = 0;
  var menuScrollLocked = false;
  var lockedScrollY = 0;

  function lockPageScroll() {
    if (menuScrollLocked) return;
    menuScrollLocked = true;
    lockedScrollY = window.scrollY;
    var body = document.body;
    body.style.position = 'fixed';
    body.style.top = '-' + lockedScrollY + 'px';
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
  }

  function unlockPageScroll() {
    if (!menuScrollLocked) return;
    var y = lockedScrollY;
    menuScrollLocked = false;
    var body = document.body;
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    body.style.overflow = '';
    window.scrollTo(0, y);
  }

  function blockBackgroundScroll(event) {
    if (!menuScrollLocked) return;
    var drawer = slideout();
    if (drawer && (event.target === drawer || drawer.contains(event.target))) return;
    event.preventDefault();
  }

  document.addEventListener('touchmove', blockBackgroundScroll, { passive: false });
  document.addEventListener('wheel', blockBackgroundScroll, { passive: false });
  window.addEventListener('scroll', function () {
    if (!menuScrollLocked && !document.body.classList.contains('mobile-nav-open')) return;
    window.requestAnimationFrame(function () {
      if (!document.body.classList.contains('mobile-nav-open')) return;
      var sticky = document.querySelector('.sticky-header');
      if (sticky) sticky.classList.add('is-stuck');
      syncMenuTop();
    });
  });

  function snapStuck() {
    var sticky = document.querySelector('.sticky-header');
    var nodes = [
      document.querySelector('header.header__wrapper'),
      document.querySelector('.header__mobile-wrapper')
    ];
    nodes.forEach(function (node) {
      if (node) node.style.transition = 'none';
    });
    if (sticky) sticky.classList.add('is-stuck');
    if (nodes[0]) nodes[0].getBoundingClientRect();
    window.requestAnimationFrame(function () {
      nodes.forEach(function (node) {
        if (node) node.style.transition = '';
      });
    });
  }

  function openDrawer(drawer) {
    syncMenuTop();
    if (!isOpen(drawer)) drawer.showPopover();
    window.requestAnimationFrame(function () {
      syncMenuTop();
    });
  }

  function stickThenOpen(drawer) {
    scrollBeforeStick = window.scrollY;
    stuckForMenu = scrollBeforeStick <= 1;
    // 头部先变成实底，同一下点击里打开。不要等页面滑完再开，否则第一次点击没有反应，连点还会把打开继续推迟。
    document.body.classList.add('mobile-nav-opening');
    snapStuck();
    openDrawer(drawer);
  }

  function releaseStuck() {
    var sticky = document.querySelector('.sticky-header');
    if (!sticky) return;
    if (stuckForMenu) {
      stuckForMenu = false;
      window.scrollTo({ top: scrollBeforeStick, behavior: 'auto' });
    }
    sticky.classList.toggle('is-stuck', window.scrollY > 0);
  }

  function setButton(open) {
    var button = document.querySelector('.js-slideout-open[aria-controls="slideout-mobile-navigation"]');
    if (!button) return;
    button.setAttribute('aria-expanded', open ? 'true' : 'false');
    button.setAttribute('aria-label', open ? 'Close navigation' : 'Open navigation');
    if (document.activeElement === button) button.blur();
  }

  document.addEventListener('mousedown', function (event) {
    var button = menuButton(event);
    if (!button) return;
    event.preventDefault();
  }, true);

  document.addEventListener('pointerdown', function (event) {
    if (event.button > 0) return;
    var button = menuButton(event);
    var drawer = slideout();
    if (!button || !drawer) return;
    event.preventDefault();
    event.stopPropagation();
    suppressClick = true;
    if (isOpen(drawer)) {
      drawer.hidePopover();
      return;
    }
    stickThenOpen(drawer);
  }, true);

  document.addEventListener('click', function (event) {
    var button = menuButton(event);
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    if (suppressClick) {
      suppressClick = false;
      return;
    }
    var drawer = slideout();
    if (!drawer || isOpen(drawer)) return;
    stickThenOpen(drawer);
  }, true);

  function bindDrawer() {
    var drawer = slideout();
    if (!drawer || drawer.dataset.headerToggleBound === 'true') return;
    drawer.dataset.headerToggleBound = 'true';
    drawer.addEventListener('toggle', function (event) {
      var open = event.newState === 'open';
      setButton(open);
      document.body.classList.toggle('mobile-nav-open', open);
      if (open) {
        lockPageScroll();
        window.requestAnimationFrame(function () {
          var sticky = document.querySelector('.sticky-header');
          if (sticky) sticky.classList.add('is-stuck');
          syncMenuTop();
        });
        window.addEventListener('resize', syncMenuTop);
      } else {
        window.removeEventListener('scroll', syncMenuTop);
        window.removeEventListener('resize', syncMenuTop);
        document.body.classList.remove('mobile-nav-opening');
        unlockPageScroll();
        releaseStuck();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindDrawer, { once: true });
  } else {
    bindDrawer();
  }
})();

/**
 * 移动端一级菜单同时只展开一项。
 * 收起上一项和滑动同时进行，滑到目标位置后立刻展开。
 * 这项展开后的高度，加上它后面还没展开的项，不满一屏时不滚到顶部，只把这段完整显示出来。
 * 交互对齐 rovdesy 的 #mobile-menu-drawer。
 */
(function () {
  var openToken = 0;

  function getNavigation() {
    return document.querySelector('#slideout-mobile-navigation .mobile__navigation--menu');
  }

  function getScroller() {
    return document.getElementById('slideout-mobile-navigation');
  }

  function getRootList(nav) {
    return nav && nav.querySelector(':scope > .mobile-menu__block .menu__list, :scope .menu__list');
  }

  function levelOneItems(nav) {
    var rootList = getRootList(nav);
    if (!rootList) return [];
    return Array.prototype.filter.call(rootList.children, function (item) {
      return item.classList && item.classList.contains('menu__item');
    });
  }

  function itemDetails(item) {
    return item.querySelector(':scope > details');
  }

  function itemHead(item) {
    var details = itemDetails(item);
    if (details) return details.querySelector(':scope > .details__summary');
    return item.querySelector(':scope > a');
  }

  function collapsedRowHeight(item) {
    var head = itemHead(item);
    if (head) return head.getBoundingClientRect().height;
    var details = itemDetails(item);
    if (details) {
      var summary = details.querySelector(':scope > .details__summary');
      if (summary) return summary.getBoundingClientRect().height;
    }
    return item.getBoundingClientRect().height;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function itemOffset(scroller, item) {
    return item.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
  }

  function maxScroll(scroller) {
    return Math.max(0, scroller.scrollHeight - scroller.clientHeight);
  }

  function openPanelHeight(details) {
    var summary = details.querySelector(':scope > .details__summary');
    var summaryHeight = summary ? summary.getBoundingClientRect().height : 0;
    return Math.max(0, details.getBoundingClientRect().height - summaryHeight);
  }

  // 收起时可见高度是 0。::details-content 可能不参与布局，克隆一份量展开后的高度。
  function expandedPanelHeight(details) {
    var content = details.querySelector(':scope > .details__content');
    if (!content) return 0;
    var visible = openPanelHeight(details);
    if (visible > 1) return visible;
    if (content.scrollHeight > 1) return content.scrollHeight;

    var probe = content.cloneNode(true);
    var summary = details.querySelector(':scope > .details__summary');
    var width = summary ? summary.getBoundingClientRect().width : details.getBoundingClientRect().width;
    probe.style.cssText = 'position:absolute;left:0;top:0;visibility:hidden;display:block;height:auto;overflow:visible;content-visibility:visible;pointer-events:none;width:' + width + 'px;';
    var host = getScroller() || document.body;
    host.appendChild(probe);
    var measured = probe.scrollHeight;
    probe.remove();
    return measured;
  }

  // 展开内容 + 后面未展开的项是否够一屏。够才滚到顶部。
  function placement(scroller, item, details, nav) {
    var items = levelOneItems(nav);
    var itemIndex = items.indexOf(item);
    var above = 0;
    var all = 0;
    var after = 0;

    items.forEach(function (otherItem, index) {
      if (index > itemIndex) after += collapsedRowHeight(otherItem);
      var other = itemDetails(otherItem);
      if (!other || other === details || !other.open) return;
      var height = openPanelHeight(other);
      all += height;
      if (index < itemIndex) above += height;
    });

    var panel = expandedPanelHeight(details);
    var fillsScreen = panel + after >= scroller.clientHeight - 1;
    // 跟 rovdesy 一样用菜单本身的高度，不用抽屉的 scrollHeight。
    // 抽屉上的 min-height: 100vh 会把可滚动距离算短，项就滑不到顶。
    var menu = scroller.querySelector('.mobile__navigation--menu');
    var scrollerPad = parseFloat(window.getComputedStyle(scroller).paddingBottom) || 0;
    var contentSize = (menu ? menu.getBoundingClientRect().height : scroller.scrollHeight) + scrollerPad;
    var finalSize = Math.max(0, contentSize - all);
    var finalOffset = Math.max(0, itemOffset(scroller, item) - above);
    var block = collapsedRowHeight(item) + panel + after;
    var pad = 0;

    if (fillsScreen) {
      // 列表比屏幕短时，差值也要补上，否则滚不到这项的顶部。
      pad = Math.max(0, Math.ceil(finalOffset + scroller.clientHeight - finalSize));
    }

    return {
      fillsScreen: fillsScreen,
      // 满一屏就贴顶。不满一屏时只上移到能看全的位置，不贴顶。
      targetVisual: fillsScreen ? 0 : Math.min(finalOffset, Math.max(0, scroller.clientHeight - block)),
      pad: pad
    };
  }

  function collapseOthers(current, nav) {
    var closing = [];
    levelOneItems(nav).forEach(function (item) {
      var other = itemDetails(item);
      if (!other || other === current || !other.open) return;
      other.removeAttribute('open');
      closing.push(other);
    });
    return closing;
  }

  function stillMoving(elements) {
    return elements.some(function (element) {
      return element.getAnimations().some(function (animation) {
        return animation.playState === 'running';
      });
    });
  }

  // 展开内容已经能撑住当前位置时，去掉为了滚到顶部临时加的空白。
  function trimPadding(list, scroller) {
    if (!list || !list.style.paddingBottom) return;
    var pad = parseFloat(list.style.paddingBottom) || 0;
    var maxWithout = scroller.scrollHeight - pad - scroller.clientHeight;
    if (maxWithout >= scroller.scrollTop - 1) list.style.paddingBottom = '';
  }

  function levelOneSummary(event) {
    var summary = event.target.closest('#slideout-mobile-navigation .details__summary');
    if (!summary) return null;
    var details = summary.parentElement;
    if (!details || details.tagName !== 'DETAILS') return null;
    var item = details.parentElement;
    if (!item || !item.classList.contains('menu__item')) return null;
    if (item.parentElement && item.parentElement.closest('.details__content')) return null;
    return summary;
  }

  function collapseAll(nav) {
    levelOneItems(nav).forEach(function (item) {
      var details = itemDetails(item);
      if (details) details.removeAttribute('open');
    });
    var list = getRootList(nav);
    if (list) list.style.paddingBottom = '';
  }

  // 捕获阶段拦住 summary 自己的开关，避免一点击就先展开。
  document.addEventListener('click', function (event) {
    var summary = levelOneSummary(event);
    if (!summary) return;

    var nav = getNavigation();
    var scroller = getScroller();
    if (!nav || !scroller) return;

    var details = summary.parentElement;
    var item = details.parentElement;
    var list = getRootList(nav);

    if (event.target.closest('a')) event.preventDefault();

    if (details.open) {
      event.preventDefault();
      details.removeAttribute('open');
      openToken += 1;
      window.setTimeout(function () {
        trimPadding(list, scroller);
      }, 220);
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    var token = ++openToken;
    var plan = placement(scroller, item, details, nav);
    if (list) list.style.paddingBottom = plan.pad > 0 ? plan.pad + 'px' : '';

    var closing = collapseOthers(details, nav);
    var expanded = false;
    var started = performance.now();
    var last = started;
    // 时间常数越大越缓。大约三个常数后停稳。
    var smoothMs = prefersReducedMotion() ? 0 : 100;

    function tick(now) {
      if (token !== openToken) return;

      var offset = itemOffset(scroller, item);
      var desired = Math.min(Math.max(0, offset - plan.targetVisual), maxScroll(scroller));
      var delta = desired - scroller.scrollTop;
      var dt = Math.min(34, Math.max(0, now - last));
      last = now;

      if (Math.abs(delta) > 0.5) {
        if (smoothMs === 0) {
          scroller.scrollTop = desired;
        } else {
          scroller.scrollTop += delta * (1 - Math.exp(-dt / smoothMs));
        }
      }

      var visual = item.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
      var arrived = Math.abs(visual - plan.targetVisual) <= 20;
      var placed = Math.abs(visual - plan.targetVisual) <= 1;
      // 已经滑到头（到顶或到内容尽头）就展开，不再等尾巴。
      var cannotCloser = Math.abs(delta) <= 1;
      if (!expanded && (arrived || cannotCloser || now - started > 380)) {
        expanded = true;
        details.setAttribute('open', '');
      }
      // 贴到目标位置之前不要撤掉临时空白，否则可滚动距离变短，会停在半路。
      if (expanded && placed) trimPadding(list, scroller);

      if (now - started > 1100) {
        scroller.scrollTop = Math.min(Math.max(0, itemOffset(scroller, item) - plan.targetVisual), maxScroll(scroller));
        if (!details.open) details.setAttribute('open', '');
        trimPadding(list, scroller);
        return;
      }
      // 跟 rovdesy 一样：收起、展开或临时空白还在时，继续把项送到目标位置。
      if (!expanded || !placed || stillMoving(closing) || stillMoving([details]) || (list && list.style.paddingBottom)) {
        window.requestAnimationFrame(tick);
      }
    }

    window.requestAnimationFrame(tick);
  }, true);

  function bindCloseReset() {
    var slideout = getScroller();
    if (!slideout || slideout.dataset.rovdesyCloseBound === 'true') return;
    slideout.dataset.rovdesyCloseBound = 'true';
    slideout.addEventListener('toggle', function (event) {
      if (event.newState !== 'closed') return;
      openToken += 1;
      var nav = getNavigation();
      if (nav) collapseAll(nav);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindCloseReset, { once: true });
  } else {
    bindCloseReset();
  }
})();

/**
 * 商品尺码：首次进入不默认选中（含排第一的 Custom Size）。
 * 仅回填本机选过的标准尺码。Custom Size 不写入记忆，刷新后也不选中。
 * 尺码对照表只在选中标准尺码后显示。
 */
(function () {
  var STORAGE_KEY = 'jovellee:lastSelectedSize';
  var applying = false;
  var userPickedSize = false;

  function normalizeSize(value) {
    return String(value || '')
      .replace(/\s*-\s*unavailable\s*$/i, '')
      .trim()
      .replace(/\s+/g, '')
      .toUpperCase();
  }

  /* 后台尺码是 US0、US2、US16W 时，页面上只显示 0、2、16W。 */
  function displaySizeLabel(value) {
    var compact = String(value || '').replace(/\s*-\s*unavailable\s*$/i, '').replace(/\s+/g, '');
    var match = compact.match(/^US(\d+W?)$/i);
    if (!match) return value || '';
    return match[1].toUpperCase();
  }

  function clearStoredSize() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      // ignore quota / private mode
    }
  }

  function readStoredSize() {
    var stored = '';
    try {
      stored = normalizeSize(window.localStorage.getItem(STORAGE_KEY));
    } catch (error) {
      return '';
    }
    if (isCustomSize(stored)) {
      clearStoredSize();
      return '';
    }
    return stored;
  }

  function writeStoredSize(value) {
    if (isCustomSize(value)) {
      clearStoredSize();
      return;
    }
    var normalized = normalizeSize(value);
    if (!normalized) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, normalized);
    } catch (error) {
      // ignore quota / private mode
    }
  }

  function isCustomSize(value) {
    return /custom/i.test(String(value || ''));
  }

  function getProductRoots(root) {
    var scope = root || document;
    var roots = scope.querySelectorAll('[data-section-type="product-section"], .product__section');
    if (roots.length) return Array.from(roots);
    if (scope.querySelector && scope.querySelector('[data-size-option="true"]')) {
      return [scope];
    }
    return [];
  }

  function getSizeGroups(productRoot) {
    return Array.from(productRoot.querySelectorAll('[data-size-option="true"]'));
  }

  function getCheckedSizeValue(group) {
    if (!group) return '';

    if (group.matches('select')) {
      return group.value || '';
    }

    var checked = group.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
    return checked ? checked.value : '';
  }

  function findMatchingControl(group, storedSize) {
    if (!storedSize) return null;

    if (group.matches('select')) {
      var options = Array.from(group.options);
      return options.find(function (option) {
        return option.value && normalizeSize(option.value) === storedSize;
      }) || null;
    }

    var inputs = Array.from(group.querySelectorAll('.swatches__form--input, input[type="radio"]'));
    return inputs.find(function (input) {
      return normalizeSize(input.value) === storedSize;
    }) || null;
  }

  var SIZE_HINT = 'please select a size';
  var CUSTOM_HINT = 'please fill in all measurements';

  function getSizeLabel(group) {
    var index = group.getAttribute('data-option-index') ||
      (group.querySelector('[data-position]') && group.querySelector('[data-position]').getAttribute('data-position'));
    if (!index) return null;

    var productRoot = group.closest('[data-section-type="product-section"], .product__section') || document;
    return productRoot.querySelector('#selected-option-' + index);
  }

  function updateSizeLabel(group, value) {
    var label = getSizeLabel(group);
    if (!label) return;
    label.textContent = value ? displaySizeLabel(value) : '';
    label.classList.remove('is-size-error', 'is-shaking');
    if (value) {
      var productRoot = group.closest('[data-section-type="product-section"], .product__section') || document;
      hideAtcHoverTips(productRoot);
    }
  }

  function setSizeHintText(productRoot, show, text) {
    getSizeGroups(productRoot).forEach(function (group) {
      var label = getSizeLabel(group);
      if (!label) return;
      if (show) {
        label.textContent = text || SIZE_HINT;
        label.classList.add('is-size-error');
      } else if (!getCheckedSizeValue(group)) {
        label.textContent = '';
        label.classList.remove('is-size-error', 'is-shaking');
      }
    });
  }

  function shakeSizeLabels(productRoot) {
    getSizeGroups(productRoot).forEach(function (group) {
      var label = getSizeLabel(group);
      if (!label) return;
      label.classList.remove('is-shaking');
      void label.offsetWidth;
      label.classList.add('is-shaking');
    });
  }

  function showMissingSize(productRoot) {
    setSizeHintText(productRoot, true);
    shakeSizeLabels(productRoot);
  }

  function getProductRootFrom(element) {
    if (element && element.closest) {
      return element.closest('[data-section-type="product-section"], .product__section') || document;
    }
    return document;
  }

  function getSelectedSizeValue(productRoot) {
    var selectedValue = '';
    getSizeGroups(productRoot).forEach(function (group) {
      if (!selectedValue) selectedValue = getCheckedSizeValue(group);
    });
    return selectedValue;
  }

  function showAtcHoverTip(button, text) {
    var parent = button.parentElement;
    if (!parent) return;
    parent.classList.add('jovellee-atc-tip-host');
    var tip = parent.querySelector(':scope > .jovellee-atc-size-tip');
    if (!tip) {
      tip = document.createElement('span');
      tip.className = 'jovellee-atc-size-tip';
      tip.setAttribute('role', 'tooltip');
      parent.appendChild(tip);
    }
    tip.textContent = text || SIZE_HINT;
    tip.classList.add('is-visible');
  }

  function hideAtcHoverTip(button) {
    var tip = button.parentElement && button.parentElement.querySelector(':scope > .jovellee-atc-size-tip');
    if (tip) tip.classList.remove('is-visible');
    button.removeAttribute('data-size-hover-active');
  }

  function hideAtcHoverTips(productRoot) {
    var scope = productRoot && productRoot.querySelectorAll ? productRoot : document;
    scope.querySelectorAll('.jovellee-atc-size-tip').forEach(function (tip) {
      tip.classList.remove('is-visible');
    });
    scope.querySelectorAll('#addToCart, .js-ajax-submit').forEach(function (button) {
      button.removeAttribute('data-size-hover-active');
    });
  }

  function needsSizeSelection(productRoot) {
    return getSizeGroups(productRoot).length > 0 && !getSelectedSizeValue(productRoot);
  }

  /** Custom Size 已选中，但还有尺码没填。没有尺码框时也视为未完成。 */
  function customSizeIncomplete(productRoot) {
    if (!isCustomSize(getSelectedSizeValue(productRoot))) return false;
    var inputs = getCustomInputs(productRoot);
    if (!inputs.length) return true;
    return inputs.some(function (input) {
      return String(input.value || '').trim() === '';
    });
  }

  function paymentHint(productRoot) {
    if (needsSizeSelection(productRoot)) return SIZE_HINT;
    if (customSizeIncomplete(productRoot)) return CUSTOM_HINT;
    return '';
  }

  function revealCustomMeasurements(productRoot) {
    var scope = productRoot && productRoot.querySelectorAll ? productRoot : document;
    scope.querySelectorAll('[data-custom-measurements], .jovellee-size-chart__custom').forEach(function (wrap) {
      wrap.hidden = false;
    });
    scope.querySelectorAll('.jovellee-size-chart [data-custom-input]').forEach(function (input) {
      input.disabled = false;
      input.required = true;
    });
    scope.querySelectorAll('.jovellee-size-chart [data-unit-property]').forEach(function (input) {
      input.disabled = false;
    });
  }

  function getCustomInputs(productRoot) {
    var scope = productRoot && productRoot.querySelectorAll ? productRoot : document;
    return Array.from(scope.querySelectorAll('.jovellee-size-chart [data-custom-input]'));
  }

  function clearCustomFieldErrors(productRoot) {
    var scope = productRoot && productRoot.querySelectorAll ? productRoot : document;
    scope.querySelectorAll('.jovellee-size-chart__field.is-error').forEach(function (field) {
      field.classList.remove('is-error');
    });
  }

  function markEmptyCustomFields(productRoot) {
    var empty = [];
    getCustomInputs(productRoot).forEach(function (input) {
      var field = input.closest('.jovellee-size-chart__field');
      var filled = String(input.value || '').trim() !== '';
      if (field) field.classList.toggle('is-error', !filled);
      if (!filled) empty.push(input);
    });
    return empty;
  }

  /** Custom Size 有任一项没填时返回 false，并标红空项。没有尺码框同样拦截。 */
  function validateCustomSize(productRoot) {
    if (!isCustomSize(getSelectedSizeValue(productRoot))) {
      clearCustomFieldErrors(productRoot);
      return true;
    }

    revealCustomMeasurements(productRoot);
    var inputs = getCustomInputs(productRoot);
    if (!inputs.length) {
      setSizeHintText(productRoot, true, CUSTOM_HINT);
      shakeSizeLabels(productRoot);
      return false;
    }

    var empty = markEmptyCustomFields(productRoot);
    if (!empty.length) return true;

    var firstEmpty = empty[0];
    var field = firstEmpty.closest('.jovellee-size-chart__field') || firstEmpty;
    if (field.scrollIntoView) {
      field.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    if (typeof firstEmpty.focus === 'function') firstEmpty.focus();
    return false;
  }

  var incompleteNoticeAt = 0;

  function blockIncompletePurchase(productRoot) {
    var incomplete = needsSizeSelection(productRoot) || customSizeIncomplete(productRoot);
    if (!incomplete) return false;

    // PayPal 会连续触发按下和点击，短时间里只提示一次。
    var now = Date.now();
    if (now - incompleteNoticeAt < 450) return true;
    incompleteNoticeAt = now;

    if (needsSizeSelection(productRoot)) {
      showMissingSize(productRoot);
      return true;
    }
    return !validateCustomSize(productRoot);
  }

  /**
   * 购买按钮在 product-form-buttons 里，尺码框在另一个表单外。
   * 优先用当前按钮所在、且带变体 id 的表单，避免写进尺码选择那个表单。
   */
  function findPurchaseForm(productRoot, source) {
    if (source && source.closest) {
      var owner = source.closest('form');
      if (owner && owner.querySelector('.js-ajax-submit, .shopify-payment-button')) return owner;
    }
    if (!productRoot || !productRoot.querySelector) return null;

    var buttonsForm = productRoot.querySelector('form[id^="product-form-buttons"]') ||
      productRoot.querySelector('form.js-prod-form-submit');
    if (buttonsForm) return buttonsForm;

    var purchaseControl = productRoot.querySelector('form[action*="/cart/add"] .js-ajax-submit, form[action*="/cart/add"] .shopify-payment-button');
    return purchaseControl ? purchaseControl.closest('form') : null;
  }

  /**
   * 尺码表在加购表单外面。表单里还有 name="id" 的变体框，
   * 用 form.id 会拿到那个输入框，尺寸框会被绑到无效的 form 上。
   * 结账按钮只读取表单内部字段，所以这里把当前尺寸写进加购表单。
   */
  function syncCustomProperties(productRoot, form) {
    if (!productRoot || !form) return;

    form.querySelectorAll('[data-jovellee-synced-property]').forEach(function (node) {
      node.remove();
    });

    var custom = isCustomSize(getSelectedSizeValue(productRoot));

    var fallbackSize = productRoot.querySelector('[data-size-fallback="true"] input:checked');
    if (fallbackSize) {
      var fallbackValue = String(displaySizeLabel(fallbackSize.value) || fallbackSize.value || '').trim();
      if (fallbackValue) {
        var fallbackInput = document.createElement('input');
        fallbackInput.type = 'hidden';
        fallbackInput.name = 'properties[Size]';
        fallbackInput.value = fallbackValue;
        fallbackInput.setAttribute('data-jovellee-synced-property', 'true');
        form.appendChild(fallbackInput);
      }
    }

    productRoot.querySelectorAll('.jovellee-size-chart [name^="properties["]').forEach(function (input) {
      var isMeasurement = input.hasAttribute('data-custom-input');
      var isUnit = input.hasAttribute('data-unit-property');
      if ((isMeasurement || isUnit) && !custom) return;

      // 数字和单位分开存，和 Add to Cart 一样：Bust: 12，Measurement Unit: INCH。
      var value = String(input.value || '').trim();
      if (!value) return;

      var hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = input.name;
      hidden.value = value;
      hidden.setAttribute('data-jovellee-synced-property', 'true');
      form.appendChild(hidden);
    });
  }

  window.jovelleeSyncSizeProperties = syncCustomProperties;

  function refreshCheckoutProperties(productRoot) {
    var form = findPurchaseForm(productRoot);
    if (!form) return;
    if (paymentHint(productRoot)) {
      form.querySelectorAll('[data-jovellee-synced-property]').forEach(function (node) {
        node.remove();
      });
      return;
    }
    syncCustomProperties(productRoot, form);
  }

  function wrapAjaxCartValidation() {
    if (!window.Shopify || !Shopify.theme || !Shopify.theme.ajaxCart) return;
    if (Shopify.theme.ajaxCart.__jovelleeSizeValidated) return;
    Shopify.theme.ajaxCart.__jovelleeSizeValidated = true;

    var originalPrepare = Shopify.theme.ajaxCart.prepareExternalLineItemProperties;
    Shopify.theme.ajaxCart.prepareExternalLineItemProperties = function (button, addToCartForm) {
      var productRoot = getProductRootFrom(button) !== document
        ? getProductRootFrom(button)
        : getProductRootFrom(addToCartForm);
      if (blockIncompletePurchase(productRoot)) return false;
      syncCustomProperties(productRoot, addToCartForm || findPurchaseForm(productRoot, button));
      if (typeof originalPrepare === 'function') {
        return originalPrepare.call(this, button, addToCartForm);
      }
      return true;
    };
  }

  function showPaymentTip(wrap, text) {
    wrap.classList.add('jovellee-atc-tip-host');
    var tip = wrap.querySelector(':scope > .jovellee-atc-size-tip');
    if (!tip) {
      tip = document.createElement('span');
      tip.className = 'jovellee-atc-size-tip';
      tip.setAttribute('role', 'tooltip');
      wrap.appendChild(tip);
    }
    tip.textContent = text;
    tip.classList.add('is-visible');
  }

  function hidePaymentTip(wrap) {
    var tip = wrap.querySelector(':scope > .jovellee-atc-size-tip');
    if (tip) tip.classList.remove('is-visible');
    wrap.removeAttribute('data-payment-hover-active');
  }

  var PAYMENT_CONTROLS = 'shopify-accelerated-checkout, shopify-paypal-button, .shopify-payment-button__more-options, .shopify-payment-button__button';

  /**
   * 不盖遮罩。按钮保持可见，未选尺码或 Custom Size 没填完时点击只提示，不进入结账。
   */
  function clearPaymentCovers(productRoot) {
    if (!productRoot || !productRoot.querySelectorAll) return;
    productRoot.querySelectorAll('.shopify-payment-button').forEach(function (wrap) {
      wrap.classList.remove('is-payment-blocked');
      wrap.style.pointerEvents = '';
      wrap.style.opacity = '';
      var guard = wrap.querySelector(':scope > .jovellee-payment-guard');
      if (guard) guard.remove();
      wrap.querySelectorAll(PAYMENT_CONTROLS).forEach(function (control) {
        control.removeAttribute('inert');
      });
    });
  }

  function showPurchaseTip(target, productRoot) {
    var hint = paymentHint(productRoot);
    if (!hint || !target || !target.closest) return;
    var pay = target.closest('.shopify-payment-button');
    if (pay) {
      showPaymentTip(pay, hint);
      return;
    }
    var button = target.closest('#addToCart, .js-ajax-submit');
    if (button) showAtcHoverTip(button, hint);
  }

  function syncPaymentGuard(productRoot) {
    if (!productRoot || !productRoot.querySelectorAll) return;
    clearPaymentCovers(productRoot);
    var blocked = Boolean(paymentHint(productRoot));
    productRoot.querySelectorAll('.shopify-payment-button').forEach(function (wrap) {
      if (blocked) {
        // 只关掉 iframe 的点击，按钮外观不变。点击会落到外壳上，由下面的监听提示。
        wrap.setAttribute('data-size-payment-blocked', 'true');
      } else {
        wrap.removeAttribute('data-size-payment-blocked');
        hidePaymentTip(wrap);
      }
    });
    if (!blocked) hideAtcHoverTips(productRoot);
    refreshCheckoutProperties(productRoot);
  }

  /**
   * 未选尺码时不加 disabled。
   * 禁用按钮收不到鼠标事件，hover 提示出不来。
   * 真正拦截加车放在点击和提交上。
   */
  function setPurchaseEnabled(productRoot, enabled) {
    productRoot.querySelectorAll('#addToCart, .js-ajax-submit').forEach(function (button) {
      if (enabled) {
        button.removeAttribute('data-size-blocked');
        if (button.getAttribute('data-size-was-disabled') !== '1') {
          button.disabled = false;
        }
        button.removeAttribute('data-size-was-disabled');
        hideAtcHoverTips(productRoot);
      } else {
        if (!button.hasAttribute('data-size-blocked')) {
          button.setAttribute('data-size-was-disabled', button.disabled ? '1' : '0');
        }
        button.setAttribute('data-size-blocked', 'true');
        if (button.getAttribute('data-size-was-disabled') !== '1') {
          button.disabled = false;
        }
      }
    });

    syncPaymentGuard(productRoot);
  }

  function syncSizeChart(selectedValue, productRoot) {
    var state = 'none';
    if (selectedValue) {
      state = isCustomSize(selectedValue) ? 'custom' : 'standard';
    }

    var scope = productRoot && productRoot.querySelectorAll ? productRoot : document;
    scope.querySelectorAll('.jovellee-size-chart').forEach(function (chart) {
      chart.setAttribute('data-size-state', state);
    });

    document.dispatchEvent(new CustomEvent('variant:change'));
  }

  function clearSizeSelection(group) {
    if (group.matches('select')) {
      group.value = '';
      Array.from(group.options).forEach(function (option) {
        option.selected = option.value === '';
      });
    } else {
      group.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach(function (input) {
        input.checked = false;
      });
    }
    updateSizeLabel(group, '');
  }

  function applySizeMemory(productRoot) {
    var groups = getSizeGroups(productRoot);
    if (!groups.length) return;
    if (userPickedSize) return;

    var storedSize = readStoredSize();
    var selectedValue = '';

    applying = true;
    groups.forEach(function (group) {
      var match = findMatchingControl(group, storedSize);
      if (match && isCustomSize(match.value)) match = null;

      if (match) {
        selectedValue = match.value;
        var alreadySelected = group.matches('select')
          ? group.value === match.value
          : match.checked;

        if (group.matches('select')) {
          group.value = match.value;
          match.selected = true;
        } else {
          match.checked = true;
        }

        updateSizeLabel(group, selectedValue);
        if (!alreadySelected) {
          group.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else {
        clearSizeSelection(group);
      }
    });
    applying = false;

    setPurchaseEnabled(productRoot, Boolean(selectedValue));
    syncSizeChart(selectedValue, productRoot);
  }

  function applyAll(scope) {
    getProductRoots(scope).forEach(applySizeMemory);
  }

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    var group = target.closest('[data-size-option="true"]');
    if (!group) return;
    if (applying) return;

    var value = getCheckedSizeValue(group);
    var productRoot = group.closest('[data-section-type="product-section"], .product__section') || document;

    if (value && isCustomSize(value) && !event.isTrusted && !userPickedSize) {
      applying = true;
      clearSizeSelection(group);
      applying = false;
      clearStoredSize();
      setPurchaseEnabled(productRoot, false);
      syncSizeChart('', productRoot);
      return;
    }

    if (value) {
      if (event.isTrusted) userPickedSize = true;
      writeStoredSize(value);
      updateSizeLabel(group, value);
      if (!isCustomSize(value)) clearCustomFieldErrors(productRoot);
    } else {
      updateSizeLabel(group, '');
      clearCustomFieldErrors(productRoot);
    }

    setPurchaseEnabled(productRoot, Boolean(value));
    syncSizeChart(value, productRoot);
  }, true);

  document.addEventListener('input', function (event) {
    var input = event.target;
    if (!(input instanceof Element) || !input.matches('[data-custom-input]')) return;
    var field = input.closest('.jovellee-size-chart__field');
    if (field && String(input.value || '').trim()) {
      field.classList.remove('is-error');
    }
    syncPaymentGuard(getProductRootFrom(input));
  }, true);

  document.addEventListener('mouseout', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    var pay = target.closest('.shopify-payment-button');
    if (pay) {
      var payRelated = event.relatedTarget;
      if (!(payRelated instanceof Node) || !pay.contains(payRelated)) {
        hidePaymentTip(pay);
      }
    }

    var button = target.closest('#addToCart, .js-ajax-submit');
    if (!button) return;
    var related = event.relatedTarget;
    if (related instanceof Node && button.contains(related)) return;

    hideAtcHoverTip(button);
  });

  function guardExpressCheckout(event) {
    var target = event.target;
    if (!(target instanceof Element)) return;
    var pay = target.closest('.shopify-payment-button, shopify-accelerated-checkout');
    if (!pay) return;
    var productRoot = getProductRootFrom(pay);
    if (paymentHint(productRoot)) {
      showPurchaseTip(pay, productRoot);
      blockIncompletePurchase(productRoot);
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }

    syncCustomProperties(productRoot, findPurchaseForm(productRoot, pay));
  }

  document.addEventListener('pointerdown', guardExpressCheckout, true);
  document.addEventListener('mousedown', guardExpressCheckout, true);
  document.addEventListener('touchstart', guardExpressCheckout, true);

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;
    var button = target.closest('#addToCart, .js-ajax-submit, .shopify-payment-button, shopify-accelerated-checkout');
    if (!button) return;

    var productRoot = getProductRootFrom(button);
    if (blockIncompletePurchase(productRoot)) {
      showPurchaseTip(button, productRoot);
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    var form = findPurchaseForm(productRoot, button);
    syncCustomProperties(productRoot, form);
  }, true);

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('form[action*="/cart/add"]')) return;

    var productRoot = getProductRootFrom(form);
    if (blockIncompletePurchase(productRoot)) {
      var submitter = event.submitter;
      if (submitter) showPurchaseTip(submitter, productRoot);
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    var purchaseForm = form.querySelector('.js-ajax-submit, .shopify-payment-button')
      ? form
      : findPurchaseForm(productRoot, form);
    syncCustomProperties(productRoot, purchaseForm);
  }, true);

  document.addEventListener('shopify:section:load', function (event) {
    applyAll(event.target);
    getProductRoots(event.target).forEach(syncPaymentGuard);
  });

  var observer = new MutationObserver(function (mutations) {
    var shouldApply = mutations.some(function (mutation) {
      return Array.from(mutation.addedNodes).some(function (node) {
        return node.nodeType === 1 && (
          node.matches?.('[data-size-option="true"], .product__variants-swatches, .product__section, .shopify-payment-button, shopify-accelerated-checkout, .shopify-payment-button__more-options') ||
          node.querySelector?.('[data-size-option="true"], .shopify-payment-button')
        );
      });
    });
    if (!shouldApply) return;
    window.setTimeout(function () {
      applyAll(document);
      getProductRoots(document).forEach(syncPaymentGuard);
    }, 0);
  });

  function boot() {
    wrapAjaxCartValidation();
    applyAll(document);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  wrapAjaxCartValidation();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();

/**
 * 快速加车弹框打开时锁住背后页面。
 * 滚轮在弹框里只滚弹框，到顶或到底也不把滚动传给后面的列表。
 */
(function () {
  var QUICKVIEW_IDS = {
    'slideout-quickview': true,
    'modal-quickview-cart': true
  };
  var lockedY = 0;
  var pinning = false;

  function openPanel() {
    return document.querySelector(
      '#slideout-quickview:popover-open, #slideout-quickview.\\:popover-open, #modal-quickview-cart:popover-open, #modal-quickview-cart.\\:popover-open'
    );
  }

  function eventInsidePanel(event, panel) {
    if (panel.contains(event.target)) return true;
    if (!event.composedPath) return false;
    return event.composedPath().indexOf(panel) !== -1;
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest('.js-quickview-trigger')) return;
    lockedY = window.scrollY;
  }, true);

  document.addEventListener('toggle', function (event) {
    var panel = event.target;
    if (!(panel instanceof Element) || !QUICKVIEW_IDS[panel.id]) return;
    if (event.newState === 'open') lockedY = window.scrollY;
  }, true);

  window.addEventListener('scroll', function () {
    if (!openPanel() || pinning) return;
    if (window.scrollY === lockedY) return;
    pinning = true;
    window.scrollTo(0, lockedY);
    pinning = false;
  }, true);

  function blockBackgroundScroll(event) {
    var panel = openPanel();
    if (!panel) return;
    if (!eventInsidePanel(event, panel)) {
      event.preventDefault();
      return;
    }

    var delta = event.deltaY || 0;
    if (!delta) return;
    var atTop = panel.scrollTop <= 0;
    var atBottom = panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 1;
    if ((delta < 0 && atTop) || (delta > 0 && atBottom)) {
      event.preventDefault();
    }
  }

  document.addEventListener('wheel', blockBackgroundScroll, { passive: false, capture: true });
  document.addEventListener('touchmove', function (event) {
    var panel = openPanel();
    if (!panel || eventInsidePanel(event, panel)) return;
    event.preventDefault();
  }, { passive: false, capture: true });
})();

/**
 * 购物车点 Checkout 前检查尺码。
 * 商品有尺码选项却没选中时拦住。
 * 选的是 Custom Size 时，对应尺码表里的尺寸属性必须都有值，缺一项也拦住。
 */
(function () {
  var SIZE_HINT = 'Please select a size before checkout.';
  var CUSTOM_HINT = 'Please fill in all measurements before checkout.';

  function cartScope(node) {
    if (node && node.closest) {
      var scoped = node.closest('.mini-cart__container, .cart-page');
      if (scoped) return scoped;
    }
    return document;
  }

  function isCheckoutPath(pathname) {
    if (/^\/(products|collections|pages|blogs)\//.test(pathname)) return false;
    return /\/checkouts?\/?$/.test(pathname) || /\/checkouts?\//.test(pathname);
  }

  function checkoutTrigger(event) {
    var path = event.composedPath ? event.composedPath() : [event.target];
    for (var i = 0; i < path.length; i++) {
      var node = path[i];
      if (!(node instanceof Element)) continue;
      if (node.matches('[name="checkout"], .ajax-cart__button-submit')) return node;
      if (node.matches('shopify-accelerated-checkout, shopify-paypal-button, .shopify-payment-button__button, .shopify-payment-button__more-options, .ajax-cart__buttons-additional')) {
        if (node.closest('.mini-cart__container, .cart-page, .js-cart-form, .ajax-cart__buttons-additional')) return node;
      }
      if (node.tagName === 'A' && node.href) {
        try {
          var url = new URL(node.href, window.location.origin);
          if (url.origin === window.location.origin && isCheckoutPath(url.pathname)) return node;
        } catch (error) {
          // ignore bad href
        }
      }
    }
    return null;
  }

  function readLineState(node) {
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  function missingCustomFields(state) {
    var required = state.required || [];
    var filled = state.filled || {};
    if (!required.length) return true;
    return required.some(function (name) {
      return String(filled[name] == null ? '' : filled[name]).trim() === '';
    });
  }

  function collectIssues(scope) {
    var issues = [];
    scope.querySelectorAll('.js-cart-size-state').forEach(function (node) {
      var state = readLineState(node);
      if (!state || !state.needsSize) return;
      var line = node.closest('.ajax-cart__cart-item');
      if (!String(state.size || '').trim()) {
        issues.push({ type: 'size', title: state.title, url: state.url, line: line });
        return;
      }
      if (state.custom && missingCustomFields(state)) {
        issues.push({ type: 'custom', title: state.title, url: state.url, line: line });
      }
    });
    return issues;
  }

  function clearNotices(scope) {
    scope.querySelectorAll('.jovellee-cart-size-notice').forEach(function (notice) {
      notice.remove();
    });
    scope.querySelectorAll('.is-cart-size-error').forEach(function (line) {
      line.classList.remove('is-cart-size-error');
    });
  }

  function issueText(issue) {
    var hint = issue.type === 'custom' ? CUSTOM_HINT : SIZE_HINT;
    return issue.title ? hint + ' ' + issue.title : hint;
  }

  function showIssues(scope, trigger, issues) {
    clearNotices(scope);
    var firstLine = null;

    issues.forEach(function (issue) {
      if (!issue.line) return;
      if (!firstLine) firstLine = issue.line;
      issue.line.classList.add('is-cart-size-error');
      var host = issue.line.querySelector('.ajax-cart__cart-variants') || issue.line;
      var note = document.createElement('p');
      note.className = 'jovellee-cart-size-notice';
      note.setAttribute('role', 'alert');
      note.textContent = issueText(issue);
      host.appendChild(note);
    });

    var summaryHost = null;
    if (trigger && trigger.closest) {
      summaryHost = trigger.closest('.ajax-cart__buttons, .ajax-cart__buttons-additional, .ajax-cart__footer-wrapper');
    }
    if (!summaryHost) summaryHost = scope.querySelector('.ajax-cart__buttons');
    if (summaryHost) {
      var summary = document.createElement('p');
      summary.className = 'jovellee-cart-size-notice';
      summary.setAttribute('role', 'alert');
      var seen = {};
      summary.textContent = issues.map(function (issue) {
        var text = issueText(issue);
        if (seen[text]) return '';
        seen[text] = true;
        return text;
      }).filter(Boolean).join(' ');
      summaryHost.insertBefore(summary, summaryHost.firstChild);
    }

    if (firstLine && firstLine.scrollIntoView) {
      firstLine.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function blockCheckout(event, trigger) {
    var scope = cartScope(trigger || event.target);
    var issues = collectIssues(scope);
    if (!issues.length) {
      clearNotices(scope);
      return false;
    }
    showIssues(scope, trigger, issues);
    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();
    return true;
  }

  function onCheckoutPointer(event) {
    var trigger = checkoutTrigger(event);
    if (!trigger) return;
    blockCheckout(event, trigger);
  }

  document.addEventListener('pointerdown', onCheckoutPointer, true);
  document.addEventListener('mousedown', onCheckoutPointer, true);
  document.addEventListener('touchstart', onCheckoutPointer, true);
  document.addEventListener('click', onCheckoutPointer, true);

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('.js-cart-form')) return;
    var submitter = event.submitter;
    if (submitter && submitter.name && submitter.name !== 'checkout') return;
    blockCheckout(event, submitter || form);
  }, true);
})();

/* 桌面详情：尺码表跟在尺码块后面。不放进 .product__variants-swatches，变体刷新会替换那里面的内容。 */
function placeProductSizeChart() {
  if (!document.body.classList.contains('template-product')) return;
  if (window.matchMedia('(max-width: 980px)').matches) return;
  var chart = document.querySelector('.jovellee-size-chart:not([data-size-chart-context="quickview"])');
  var swatches = document.querySelector('.product__section-content .product__variants-swatches');
  if (!chart || !swatches || chart.previousElementSibling === swatches) return;
  swatches.insertAdjacentElement('afterend', chart);
}

document.addEventListener('DOMContentLoaded', placeProductSizeChart);
window.addEventListener('load', placeProductSizeChart);
placeProductSizeChart();

/* 活动日期按 MM/DD/YYYY 显示。真正提交的仍是日期字段。 */
function bindEventDateFormat(root) {
  var scope = root || document;
  scope.querySelectorAll('[data-event-date]').forEach(function (dateInput) {
    if (dateInput.dataset.formatBound === 'true') return;

    var display = dateInput.parentElement && dateInput.parentElement.querySelector('.jovellee-size-chart__date');
    if (!display || display === dateInput) {
      if (dateInput.type !== 'date') return;
      var wrap = document.createElement('div');
      wrap.className = 'jovellee-size-chart__date-wrap';
      dateInput.parentNode.insertBefore(wrap, dateInput);
      display = document.createElement('input');
      display.type = 'text';
      display.className = 'jovellee-size-chart__date';
      display.placeholder = 'MM/DD/YYYY';
      display.readOnly = true;
      display.autocomplete = 'off';
      if (dateInput.id) {
        display.id = dateInput.id;
        dateInput.removeAttribute('id');
      }
      dateInput.className = 'jovellee-size-chart__date-value';
      wrap.appendChild(display);
      wrap.appendChild(dateInput);
    }

    var wrap = dateInput.closest('.jovellee-size-chart__date-wrap');
    display.readOnly = true;
    display.tabIndex = -1;
    display.removeAttribute('inputmode');
    display.removeAttribute('maxlength');
    dateInput.tabIndex = -1;
    dateInput.setAttribute('aria-hidden', 'true');
    dateInput.dataset.formatBound = 'true';

    var opener = wrap.querySelector('.jovellee-size-chart__date-open');
    if (!opener) {
      opener = document.createElement('button');
      opener.type = 'button';
      opener.className = 'jovellee-size-chart__date-open';
      wrap.appendChild(opener);
    }
    if (display.id) {
      opener.id = display.id;
      display.removeAttribute('id');
    } else if (dateInput.id) {
      opener.id = dateInput.id;
      dateInput.removeAttribute('id');
    }
    opener.setAttribute('aria-label', 'Choose event date');

    function toDisplay(value) {
      var parts = (value || '').split('-');
      if (parts.length !== 3 || !parts[0]) return '';
      return parts[1] + '/' + parts[2] + '/' + parts[0];
    }

    function openPicker() {
      if (typeof dateInput.showPicker !== 'function') return;
      try {
        dateInput.showPicker();
      } catch (error) {}
    }

    dateInput.addEventListener('change', function () {
      display.value = toDisplay(dateInput.value);
    });

    dateInput.addEventListener('keydown', function (event) {
      if (event.key === 'Tab' || event.key === 'Escape') return;
      event.preventDefault();
    });

    var fromPointer = false;

    opener.addEventListener('pointerdown', function () {
      fromPointer = true;
    });

    opener.addEventListener('click', function (event) {
      event.preventDefault();
      openPicker();
    });

    opener.addEventListener('focus', function () {
      if (fromPointer) {
        fromPointer = false;
        return;
      }
      openPicker();
    });

  });
}

document.addEventListener('DOMContentLoaded', function () { bindEventDateFormat(); });
bindEventDateFormat();

/* 详情标题旁的分享：底部弹出 Facebook / X / Pinterest / 邮件 / 复制链接。 */
(function () {
  var sheet = document.querySelector('.pdp-share');
  var opener = null;

  function placeShare() {
    if (!sheet) return;
    document.body.appendChild(sheet);
  }

  if (sheet) placeShare();

  function copyText(text) {
    function fallback() {
      var input = document.createElement('textarea');
      input.value = text;
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.left = '-9999px';
      document.body.appendChild(input);
      input.select();
      try { document.execCommand('copy'); } catch (error) {}
      input.remove();
      return Promise.resolve();
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text).catch(fallback);
    }
    return fallback();
  }

  function openShare(button) {
    if (!sheet) return;
    placeShare();
    opener = button;
    var status = sheet.querySelector('.pdp-share__status');
    if (status) status.textContent = '';
    sheet.hidden = false;
    if (typeof sheet.showModal === 'function' && !sheet.open) {
      sheet.showModal();
    }
    sheet.classList.add('is-open');
    document.documentElement.classList.add('pdp-share-open');
    var closeButton = sheet.querySelector('.pdp-share__close');
    if (closeButton) closeButton.focus();
  }

  function closeShare() {
    if (!sheet || (sheet.hidden && !sheet.open)) return;
    sheet.classList.remove('is-open');
    document.documentElement.classList.remove('pdp-share-open');
    if (sheet.open) sheet.close();
    sheet.hidden = true;
    if (opener) {
      opener.focus();
      opener = null;
    }
  }

  if (sheet) {
    sheet.addEventListener('close', function () {
      sheet.classList.remove('is-open');
      sheet.hidden = true;
      document.documentElement.classList.remove('pdp-share-open');
    });
    sheet.addEventListener('cancel', function (event) {
      event.preventDefault();
      closeShare();
    });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('.product-title-share');
    if (button) {
      event.preventDefault();
      openShare(button);
      return;
    }
    if (!sheet || sheet.hidden) return;
    if (event.target.closest('[data-share-close]')) {
      event.preventDefault();
      closeShare();
      return;
    }
    var copyButton = event.target.closest('[data-share-copy]');
    if (!copyButton) return;
    event.preventDefault();
    var url = copyButton.getAttribute('data-share-url');
    var status = sheet.querySelector('.pdp-share__status');
    copyText(url).then(function () {
      if (status) status.textContent = 'Link copied';
    });
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeShare();
  });
})();

/* Size chart 按钮打开右侧尺码抽屉，并跟上当前选中的尺码。 */
(function () {
  var lastFocus = null;
  var sizeRoot = null;

  /* 详情页的抽屉挂到 body，用 manual popover 盖住页面。 */
  function hoist(panel) {
    if (panel.parentElement !== document.body) {
      document.body.appendChild(panel);
    }
    if (!panel.hasAttribute('popover')) {
      panel.setAttribute('popover', 'manual');
    }
  }

  function quickviewHost() {
    return document.querySelector(
      '#slideout-quickview:popover-open, #slideout-quickview.\\:popover-open, #modal-quickview-cart:popover-open, #modal-quickview-cart.\\:popover-open'
    ) || document.getElementById('slideout-quickview') || document.getElementById('modal-quickview-cart');
  }

  /* 快速加购的尺码表必须留在弹层里面。单独再开一个 popover 时，点关闭或蒙层会被当成点在弹层外，快速加购一起被收起。 */
  function mountInQuickview(panel) {
    var host = quickviewHost();
    if (!host) return false;
    if (panel.hidePopover && panel.matches(':popover-open')) panel.hidePopover();
    panel.removeAttribute('popover');
    panel.removeAttribute('hidden');
    host.appendChild(panel);
    host.classList.add('has-size-guide');
    panel.classList.add('pdp-size-guide--in-quickview');
    return true;
  }

  function sizeKeyFromValue(value) {
    var raw = String(value || '').trim();
    var letterInLabel = raw.match(/\(([^)]+)\)/);
    if (letterInLabel) raw = letterInLabel[1];
    var key = raw
      .split('/')[0]
      .replace(/\s+/g, '')
      .replace(/^us/i, '')
      .toUpperCase();
    var child = key.match(/^(?:CHILD-?|C)(\d{1,2})$/);
    if (child) return 'C' + child[1];
    var alphaMap = {
      XXL: '2XL',
      XXXL: '3XL',
      XXXXL: '4XL',
      XXXXXL: '5XL',
      XXXXXXL: '6XL'
    };
    return alphaMap[key] || key;
  }

  function selectedSizeKey(root) {
    var scope = root || document;
    var checked = scope.querySelector('[data-size-option="true"] input:checked');
    return checked ? sizeKeyFromValue(checked.value) : '';
  }

  function currentUnit(panel) {
    var pressed = panel.querySelector('[data-size-unit][aria-pressed="true"]');
    return pressed && pressed.getAttribute('data-size-unit') === 'cm' ? 'cm' : 'inch';
  }

  function highlightSize(panel) {
    if (!panel) return;
    var key = selectedSizeKey(sizeRoot);
    var selected = null;
    panel.querySelectorAll('[data-size-key]').forEach(function (row) {
      var match = sizeKeyFromValue(row.getAttribute('data-size-key')) === key && key !== '';
      row.classList.toggle('is-selected', match);
      if (match) selected = row;
    });
    if (!selected) return;
    var scroller = panel.querySelector('.pdp-size-guide__body') || panel.querySelector('.pdp-size-guide__panel');
    if (!scroller) return;
    var rowTop = selected.getBoundingClientRect().top;
    var scrollerTop = scroller.getBoundingClientRect().top;
    scroller.scrollTop += rowTop - scrollerTop - 8;
  }

  function applyUnit(panel, nextUnit) {
    var unit = nextUnit === 'cm' ? 'cm' : 'inch';
    panel.querySelectorAll('[data-inch]').forEach(function (cell) {
      cell.textContent = cell.getAttribute(unit === 'cm' ? 'data-cm' : 'data-inch') || '';
    });
    panel.querySelectorAll('[data-size-unit]').forEach(function (button) {
      button.setAttribute('aria-pressed', button.getAttribute('data-size-unit') === unit ? 'true' : 'false');
    });
  }

  function formatGuideNumber(value) {
    if (value === null || value === undefined || value === '') return '';
    var number = Number(value);
    return isFinite(number) ? String(number) : String(value);
  }

  function displaySizeLabel(value) {
    var match = String(value || '').replace(/\s+/g, '').match(/^US(\d+W?)$/i);
    if (!match) return value || '';
    return match[1].toUpperCase();
  }

  /* 各品类尺码表都从尺码目录按商品标签画出来。 */
  function renderCategoryChart(panel) {
    var type = panel.getAttribute('data-chart-type') || 'women-dress';
    var dataId = panel.getAttribute('data-size-data-id');
    if (panel.getAttribute('data-chart-rendered') === dataId) return;
    var dataElement = dataId ? document.getElementById(dataId) : null;
    var table = panel.querySelector('[data-size-guide-table]');
    if (!dataElement || !table) return;

    var configs;
    try {
      configs = JSON.parse(dataElement.textContent);
    } catch (error) {
      return;
    }

    var config = configs[type];
    if (!config || !Array.isArray(config.columns) || !Array.isArray(config.data)) return;

    var unit = currentUnit(panel);
    table.innerHTML = '';
    table.setAttribute('data-dynamic', 'true');
    table.style.setProperty('--size-cols', String(config.columns.length + 1));

    var head = document.createElement('div');
    head.className = 'pdp-size-guide__row pdp-size-guide__row--head';
    var sizeHeading = document.createElement('span');
    sizeHeading.textContent = config.sizeLabel || 'US Size';
    head.appendChild(sizeHeading);
    config.columns.forEach(function (column) {
      var heading = document.createElement('span');
      heading.textContent = column.label;
      head.appendChild(heading);
    });
    table.appendChild(head);

    config.data.forEach(function (row) {
      var line = document.createElement('div');
      line.className = 'pdp-size-guide__row';
      line.setAttribute('data-size-key', row.size || '');

      var sizeCell = document.createElement('span');
      sizeCell.textContent = displaySizeLabel(row.size || '');
      line.appendChild(sizeCell);

      config.columns.forEach(function (column) {
        var measurement = row[column.key] || {};
        var cell = document.createElement('span');
        var inch = formatGuideNumber(measurement.inch);
        var cm = formatGuideNumber(measurement.cm);
        cell.setAttribute('data-inch', inch);
        cell.setAttribute('data-cm', cm);
        cell.textContent = unit === 'cm' ? cm : inch;
        line.appendChild(cell);
      });
      table.appendChild(line);
    });
    panel.setAttribute('data-chart-rendered', dataId);
  }

  /* 快速加车的抽屉是接口注入的，要点击时再找到当前商品那一份。 */
  function drawerFor(link) {
    var quickRoot = link.closest('.js-quickview-wrapper');
    if (quickRoot) {
      var local = quickRoot.querySelector('[data-size-guide]');
      if (!local) {
        var productId = quickRoot.getAttribute('data-product-id');
        if (productId) {
          local = document.querySelector(
            '[data-size-guide][data-size-guide-context="quickview"][data-size-guide-product="' + productId + '"]'
          );
        }
      }
      if (!local) return null;
      document.querySelectorAll('[data-size-guide][data-size-guide-context="quickview"]').forEach(function (node) {
        if (node === local) return;
        node.classList.remove('is-open');
        if (node.hidePopover && node.matches(':popover-open')) node.hidePopover();
        node.remove();
      });
      return { panel: local, root: quickRoot };
    }

    var panel = document.querySelector('[data-size-guide]:not([data-size-guide-context="quickview"])');
    if (!panel) return null;
    hoist(panel);
    return { panel: panel, root: link.closest('.product__section') || document };
  }

  function openDrawer(panel, trigger, root) {
    document.querySelectorAll('[data-size-guide].is-open').forEach(function (other) {
      if (other !== panel) closeDrawer(other);
    });
    renderCategoryChart(panel);
    sizeRoot = root || null;
    lastFocus = trigger || document.activeElement;
    var inQuickview = panel.getAttribute('data-size-guide-context') === 'quickview';
    if (inQuickview) {
      if (!mountInQuickview(panel)) return;
    } else if (panel.showPopover && !panel.matches(':popover-open')) {
      panel.showPopover();
    }
    panel.removeAttribute('hidden');
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        if (!panel.isConnected) return;
        panel.classList.add('is-open');
        panel.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('pdp-size-guide-open');
        highlightSize(panel);
        var closeButton = panel.querySelector('.pdp-size-guide__close');
        if (closeButton) closeButton.focus();
      });
    });
  }

  function closeDrawer(panel) {
    if (!panel) return;
    var inQuickview = panel.classList.contains('pdp-size-guide--in-quickview');
    panel.classList.remove('is-open');
    panel.classList.toggle('is-closing', inQuickview);
    panel.setAttribute('aria-hidden', 'true');
    if (!document.querySelector('[data-size-guide].is-open')) {
      document.documentElement.classList.remove('pdp-size-guide-open');
    }
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    window.setTimeout(function () {
      if (panel.classList.contains('is-open')) return;
      panel.classList.remove('is-closing');
      var host = panel.closest('#slideout-quickview, #modal-quickview-cart');
      if (host) host.classList.remove('has-size-guide');
      if (inQuickview) {
        panel.setAttribute('hidden', '');
        return;
      }
      if (panel.hidePopover && panel.matches(':popover-open')) panel.hidePopover();
    }, 320);
  }

  var pageDrawer = document.querySelector('[data-size-guide]:not([data-size-guide-context="quickview"])');
  if (pageDrawer) {
    hoist(pageDrawer);
    renderCategoryChart(pageDrawer);
  }

  document.addEventListener('click', function (event) {
    if (!event.target || !event.target.closest) return;
    var link = event.target.closest('.swatches__size-chart-link');
    if (link) {
      event.preventDefault();
      event.stopPropagation();
      var target = drawerFor(link);
      if (!target) return;
      /* 等这次点击的 popover 轻触关闭跑完，再打开，避免刚打开就被收起。 */
      window.setTimeout(function () {
        openDrawer(target.panel, link, target.root);
      }, 0);
      return;
    }

    var guide = event.target.closest('[data-size-guide]');
    if (!guide || !guide.classList.contains('is-open')) return;
    event.stopPropagation();

    if (event.target.closest('[data-size-guide-close]')) {
      event.preventDefault();
      closeDrawer(guide);
      return;
    }

    var unitButton = event.target.closest('[data-size-unit]');
    if (unitButton) applyUnit(guide, unitButton.getAttribute('data-size-unit'));
  }, true);

  document.addEventListener('change', function (event) {
    var option = event.target.closest('[data-size-option="true"]');
    if (!option) return;
    var openPanel = document.querySelector('[data-size-guide].is-open');
    if (!openPanel) return;
    if (sizeRoot && !sizeRoot.contains(option)) return;
    highlightSize(openPanel);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var openPanel = document.querySelector('[data-size-guide].is-open');
    if (!openPanel) return;
    event.preventDefault();
    event.stopPropagation();
    closeDrawer(openPanel);
  }, true);

  /* 个别浏览器仍会把快速加购当成被点到外面。尺码表还开着时把它拉回来。 */
  document.addEventListener('toggle', function (event) {
    var host = event.target;
    if (!host || (host.id !== 'slideout-quickview' && host.id !== 'modal-quickview-cart')) return;
    if (event.newState !== 'closed') return;
    if (!document.querySelector('[data-size-guide][data-size-guide-context="quickview"].is-open, [data-size-guide][data-size-guide-context="quickview"].is-closing')) return;
    if (typeof host.showPopover !== 'function') return;
    try {
      host.showPopover();
    } catch (error) {}
  }, true);
})();

